import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { TemplateEditorStateExport } from 'components/VideoTemplateEditor';
import { IndexedStep } from 'components/Wizard';
import useMergeFlows from 'containers/ProjectWizards/useMergeFlows';
import useOnMount from 'hooks/useOnMount';
import * as mixpanelActions from 'redux/modules/mixpanel/actions';
import { MixpanelWizardStep } from 'redux/modules/mixpanel/types';
import { clearNotification } from 'redux/modules/notification';
import { clearCreationSuggestionsProgress } from 'redux/modules/wizard-export/actions';
import { Dispatch } from 'redux/types';
import { trackStepChange, videoFlowError, videoFlowSubmit } from './actions';
import { ERROR_ID } from './constants';
import { useVideoFlowState } from './state';
import {
  UseVideoFlow,
  VideoFlowInternalErrorHandler,
  VideoFlowStep,
} from './types';
import useCustomizeStep from './useCustomizeStep';
import useExportStep from './useExportStep';
import useObjectiveStep from './useObjectiveStep';
import useProgressStep from './useProgressStep';
import useVideoFlowBlocker from './useVideoFlowBlocker';
import useVideoSubFlow from './useVideoSubFlow';
import { block, canBacktrackFromStep } from './utils';

const useVideoFlow: UseVideoFlow = ({
  defaultAspectRatio,
  defaultLanguage,
  defaultObjective,
  isAspectRatioLocked,
  onError,
  onTemplateSelect,
  wizardRef,
  isTemplateLocked,
  skipTemplateSelection,
  stepId,
  templateId,
}) => {
  const dispatch = useDispatch<Dispatch>();
  const [state, stateDispatch] = useVideoFlowState({
    defaultAspectRatio,
    defaultLanguage,
    defaultObjective,
    isAspectRatioLocked,
  });
  const { isObjectiveStepComplete, objective } = state;
  const previousIsObjectiveStepComplete = useRef(isObjectiveStepComplete);

  useOnMount(() => {
    dispatch(clearCreationSuggestionsProgress());
    dispatch(clearNotification(ERROR_ID));
    dispatch(mixpanelActions.clearMixpanel());
  });

  const handleStepChange = useCallback(
    (
      toStep: IndexedStep<VideoFlowStep>,
      from?: IndexedStep<VideoFlowStep>,
      _action?: unknown,
      steps = [],
    ) => {
      stateDispatch({
        type: 'step-change',
        payload: {
          toStep,
          fromStep: from,
          steps,
        },
      });
      const isAspectRatioStep =
        from?.stepId === VideoFlowStep.OBJECTIVE &&
        objective === 'SingleClip' &&
        !state.isAspectRatioLocked;

      dispatch(
        trackStepChange(from?.stepId as VideoFlowStep, {
          type: 'videoTranscription',
          step: isAspectRatioStep
            ? 'Choose Aspect Ratio'
            : (from?.stepId as MixpanelWizardStep),
          ...(from?.stepId === VideoFlowStep.OBJECTIVE &&
            !isAspectRatioStep && {
              objectiveSelection: objective,
            }),
        }),
      );
    },
    [dispatch, objective, state.isAspectRatioLocked, stateDispatch],
  );

  const handleError: VideoFlowInternalErrorHandler = useCallback(
    (error, file, reason) => {
      const handlingResult = dispatch(videoFlowError(error, file, reason));

      if (handlingResult === 'unhandled') {
        onError(error);
      }
    },
    [dispatch, onError],
  );

  // use an effect to advance to the next step when an objective changes.  this
  // gives the flow time to render the subflow steps before trying to switch to them
  useEffect(() => {
    if (isObjectiveStepComplete !== previousIsObjectiveStepComplete.current) {
      previousIsObjectiveStepComplete.current = isObjectiveStepComplete;

      if (isObjectiveStepComplete) {
        wizardRef.current?.next();
      }
    }
  }, [isObjectiveStepComplete, wizardRef]);

  const warnOnLeaving = useVideoFlowBlocker(stepId);

  const subflow = useVideoSubFlow({
    onError: handleError,
    rootStepId: VideoFlowStep.OBJECTIVE,
    state,
    stateDispatch,
    stepId,
    wizardRef,
  });

  const { step: objectiveStep } = useObjectiveStep({
    completedName: subflow.rootStepCompletedName,
    state,
    stateDispatch,
  });

  const handleSubmit = useCallback(
    (exportState: TemplateEditorStateExport) => {
      stateDispatch({
        type: 'submit',
        payload: { exportState },
      });
      dispatch(
        videoFlowSubmit({
          aspectRatioName: state.aspectRatioName,
          clipStartMillis: state.clipStartMillis,
          clipEndMillis: state.clipEndMillis,
          exportState,
          isAutoFrame: state.isAutoFrame,
          isFullEpisode: state.isFullEpisode,
          language: state.language,
          mediaImportedArgs: state.mediaImportedArgs,
          objective,
          projectName: state.projectName,
          suggestedClipDurationMillis: state.suggestedClipDurationMillis,
          transcribe: state.transcribe,
          platform: state.platform,
          useYoutubeMostReplayed: state.useYoutubeMostReplayed,
          videoDurationMillis: state.videoDurationMillis,
          videoFile: state.videoSrc,
          videoSrc: state.videoSrcUrl,
          videoType: state.videoType,
        }),
      );
      subflow.onSubmit?.();

      if (objective === 'SingleClip' || objective === 'YouTubeMostReplayed') {
        wizardRef.current.jump(VideoFlowStep.PROGRESS);
      } else {
        wizardRef.current.jump(VideoFlowStep.EXPORT);
      }
    },
    [dispatch, objective, state, stateDispatch, subflow, wizardRef],
  );

  const { step: customizeStep } = useCustomizeStep({
    defaultView:
      skipTemplateSelection || isTemplateLocked ? 'customize' : 'template',
    isTemplateLocked,
    onSubmit: handleSubmit,
    onTemplateSelect,
    state,
    stateDispatch,
    templateId,
  });

  const { step: progressStep } = useProgressStep({
    isFullEpisode: state.isFullEpisode,
    objective,
    onError: handleError,
    wizardRef,
  });

  const { step: exportStep } = useExportStep({
    isFullEpisode: state.isFullEpisode,
    mainVideoSrc: state.videoSrc,
    onError: handleError,
    videoFileSize: state.videoSrc?.size,
  });

  const rootFlow = {
    bodyClassName: block('body', { [stepId]: !!stepId }),
    buttonClassName: block('buttons', {
      'free-width': stepId === VideoFlowStep.CLIP_VIDEO,
    }),
    canBacktrack: canBacktrackFromStep(stepId),
    onStepChange: handleStepChange,
    onStepClick: warnOnLeaving,
    steps: [objectiveStep, customizeStep, progressStep, exportStep],
  };

  return useMergeFlows(rootFlow, subflow, VideoFlowStep.OBJECTIVE);
};

export default useVideoFlow;
